import { computed, reactive, watch, ref, toRaw } from 'vue';
import { useRoute } from 'vue-router';
import { useAsyncState } from '@vueuse/core';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { legacyApi, utils as piniaUtils } from '@dashboardApp/store';
import { performAnalyticsTracking } from '@dashboardApp/helpers/performAnalyticsTracking';
import { performSupplierLocalTracking } from '@dashboardApp/helpers/performSupplierLocalTracking';
import useNotifications from '@dashboardApp/composables/useNotifications';
import router from '@dashboardApp/router';

const { addAlert } = useNotifications();

export const useDirectorySearchStore = defineStore('DirectorySearchStore', () => {

    const supplierLocalTracking = performSupplierLocalTracking();
    const route = useRoute();
    const { track: trackingEvent } = performAnalyticsTracking();

    const defaultGetFilters = {
        type: [],
        ship_to: [],
        ship_from: [],
        country: [],
        ships_internationally: null,
        ships_worldwide: null,
        minimum_order_value: -1,
        allowed_marketplaces: [],
        classifications: [],
        best_ranked: null,
        sort: 'relevance',
        source: [],
        fast_shipping: null,
        delivery_min: null,
        delivery_max: null,
        price_min: null,
        price_max: null,
        orders_min: null,
        orders_max: null,
        rating_min: null,
        hide_adult: null,
        exclude_import_list: null
    };

    function clonePlainState(state) {
        return Object.fromEntries(
            Object.entries(toRaw(state)).map(([key, value]) => {
                if (Array.isArray(value)) {
                    return [key, [...value]];
                }

                if (value && typeof value === 'object') {
                    return [key, { ...value }];
                }

                return [key, value];
            })
        );
    }

    const getFilters = reactive(clonePlainState(defaultGetFilters));
    const postFilters = reactive({
        search_type: searchTypeByRouteName(),
        category: 'all',
        query: '',
        nosuggest: false
    });
    const results = reactive({
        products: [],
        suppliers: []
    });
    const resultCounts = reactive({
        products: 0,
        suppliers: 0
    });
    const searchView = reactive({
        id: '',
        view: '',
        active: false
    });
    const searchMeta = reactive({
        hasProductCatalog: false,
        isKeywordMatchCatalog: false,
        querySuggest: null,
        searchTermMessage: null,
        adjustedSearchResultTotal: null,
        isDefaultSearchParams: false
    });
    const querySuggest = reactive({
        newSearchTerm: null,
        originalSearchTerm: null
    });

    const searchIsReadyState = reactive({
        products: false,
        suppliers: false
    });
    const searchIsLoadingState = reactive({
        products: false,
        suppliers: false
    });

    const hasFilters = computed(() => Object.values(getFilters).filter((val) => val).length);
    const hasResults = computed(() => resultCounts.products + resultCounts.suppliers);
    const hasProductResults = computed(() => resultCounts.products);
    const hasSupplierResults = computed(() => resultCounts.suppliers);
    const isDefaultSearchParams = computed(() => searchMeta.isDefaultSearchParams);

    const adjustedSearchResultTotal = computed(() => searchMeta.adjustedSearchResultTotal);

    const showEmptyResults = computed(() => {
        return searchCompleted.value && hasFilters.value && postFilters.query && !hasResults.value;
    });
    const getFiltersApplied = computed(() => {
        for (const [key, value] of Object.entries(getFilters)) {
            const rawValue = toRaw(value);
            if (Array.isArray(rawValue)) {
                const diff = rawValue.filter(x => !defaultGetFilters[key].includes(x));
                if (diff.length > 0) {
                    return true;
                }
                continue;
            } 

            if (defaultGetFilters[key] != rawValue) {
                return true;
            }
        }
        return false;
    });

    const searchCompleted = ref(false);
    const searchTerm = ref('');

    let preSearchTriggered = false;
    let preSearchController = null;
    let searchController = null;
    let preSearchRequestId = 0;
    let searchRequestId = 0;

    const cloneState = clonePlainState;

    function isCanceledRequest(error) {
        return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
    }

    const { execute: preSearch, isReady: preSearchIsReady, isLoading: preSearchIsLoading } = useAsyncState(
        preSearchPromise,
        {},
        { immediate: false }
    );

    function preSearchPromise() {
        const requestId = ++preSearchRequestId;
        const postFiltersSnapshot = cloneState(postFilters);
        const getFiltersSnapshot = cloneState(getFilters);

        if (preSearchController) {
            preSearchController.abort();
        }

        if (searchController) {
            searchController.abort();
        }

        preSearchController = new AbortController();
        searchCompleted.value = false;

        return legacyApi.post(
            '/directory/all',
            { data: postFiltersSnapshot },
            { params: getFiltersSnapshot, signal: preSearchController.signal }
        )
            .then(({ data }) => {
                if (requestId !== preSearchRequestId) {
                    return;
                }

                preSearchTriggered = true;
                Object.assign(searchView, data.searchView);
                Object.assign(searchMeta, data.meta);
                Object.assign(querySuggest, data.meta.querySuggest);

                if (data.meta.searchTermMessage) {
                    postFilters.nosuggest = true;
                }

                router.push(
                    postFiltersSnapshot.search_type == 'products' 
                        ? `/directory/${data.view}`
                        : `/directory/${data.view}/suppliers`
                );
            })
            .catch((err) => {
                if (isCanceledRequest(err)) {
                    return;
                }
                throw err;
            })
            .finally(() => {
                if (requestId === preSearchRequestId) {
                    preSearchController = null;
                }
            });
    }

    const { execute: search, isReady: searchIsReady, isLoading: searchIsLoading } = useAsyncState(
        searchPromise,
        {},
        { immediate: false }
    );

    function searchPromise(args) {
        const requestId = ++searchRequestId;
        const view = args?.view || null;
        const page = args?.page || 1;
        const postFiltersSnapshot = cloneState(postFilters);

        if (!view) {
            return Promise.reject();
        }

        if (searchController) {
            searchController.abort();
        }

        searchController = new AbortController();

        let url = `/directory/${view}`;
        if (page && page > 1) {
            url += `/${page}`;
        }

        let params = {};
        if (postFiltersSnapshot.nosuggest) {
            params.nosuggest = true;
        }

        return legacyApi.post(url, {}, { params, signal: searchController.signal }).then(({ data }) => {
            if (requestId !== searchRequestId) {
                return;
            }

            for (let key in data.searchData) {
                if (key in postFilters) {
                    postFilters[key] = data.searchData[key];
                } else if (key in getFilters) {
                    getFilters[key] = data.searchData[key];
                }
            }
            Object.assign(searchView, data.searchView);

            // Reset
            results[postFiltersSnapshot.search_type] = [];

            Object.assign(results[postFiltersSnapshot.search_type], data.searchResults);
            Object.assign(resultCounts, data.searchResultTotal);
            Object.assign(searchMeta, data.searchMeta);
            Object.assign(querySuggest, data.searchMeta.querySuggest);
            searchTerm.value = postFilters.query;
        }).then(() => {
            if (requestId !== searchRequestId) {
                return;
            }

            if (preSearchTriggered) {
                preSearchTriggered = false;
                trackDirectorySearch();
                trackKeywordMatchCatalog();
                trackRegisteredSupplierDirectoryDisplayed();
            }
            
            trackCatalogFilterDisplayed();
            trackDirectoryProductSearch();

            if (postFilters.search_type === 'suppliers') {
                supplierLocalTracking.includedInResults(view, supplierResultsToIDs());
            }
            searchCompleted.value = true;
        }).catch((err) => {
            if (isCanceledRequest(err)) {
                return;
            }

            if (err?.response?.status === 404) {
                router.replace({ name: 'shone-dashboard' });
                addAlert({ message: 'Invalid search.', type: 'error' });
            }
        }).finally(() => {
            if (requestId === searchRequestId) {
                searchController = null;
            }
        });
    }

    function loadResults(params, routeName) {
        if (!params) {
            params = route.params;
        }

        if (!routeName) {
            routeName = route.name;
        }

        postFilters.search_type = searchTypeByRouteName(routeName);

        if (params.view && params.view !== 'all') {
            search(0, { view: params.view, page: params.page || 1 });
        } else {
            preSearch();
        }
    }

    function requestManualSearch(formData) {
        return legacyApi.post('/supplier-finder/custom-request', { data: formData }).then(({ data }) => {
            // SaleHoo.TrackedEvents.track('customSupplierDirect', { product, supplier_type, location });
            return data;
        });
    }
    
    function selectQueryAndPreSearch(query, nosuggest = null, search_type = null) {
        postFilters.query = query;
        postFilters.nosuggest = nosuggest;
        if (search_type) {
            postFilters.search_type = search_type;
        }
        resetBadQueryMessage();
        resetTermSuggestion();
        return preSearch();
    }

    function resetFilters(resetPostFilters = true) {
        Object.assign(getFilters, clonePlainState(defaultGetFilters));
        if (resetPostFilters) {
            Object.assign(postFilters, {
                search_type: searchTypeByRouteName(),
                category: 'all',
            });
        }
    }

    function trackDirectorySearch() {
        if (postFilters.query === '') {
            return;
        }
        const attrs = {
            'directorySearchKeyword': postFilters.query,
            'directorySearchType': postFilters.search_type,
            'directoryCategory': postFilters.category
        };

        trackingEvent('dl.directorySearch', attrs);
        trackingEvent('directorySearch', attrs);
    }

    function trackKeywordMatchCatalog() {
        if (searchMeta.isKeywordMatchCatalog) {
            trackingEvent('dl.keywordMatchCatalog');
        }
    }

    function trackRegisteredSupplierDirectoryDisplayed() {
        if (resultCounts.suppliers === 0) {
            return;
        }
        trackingEvent('dl.registeredSupplierDirectoryDisplayed', {
            registeredSupplierCount: resultCounts.suppliers
        });
    }


    function trackCatalogFilterDisplayed() {
        if (searchMeta.hasProductCatalog) {
            trackingEvent('dl.catalogFilterDisplayed');
        }
    }

    function trackDirectoryProductSearch() {
        if (postFilters.search_type !== 'products') {
            return;
        }
        
        trackingEvent('directoryProductSearch', {
            directorySearchKeyword: postFilters.query || ''
        });
    }

    function supplierResultsToIDs() {
        const suppliers = [];
        for (const [, result] of Object.entries(results.suppliers)) {
            suppliers.push(result.SupplierLive.id);
        }
        return suppliers;
    }

    function searchTypeByRouteName(routeName) {
        if (!routeName) {
            routeName = route.name;
        }

        if (typeof routeName !== 'string') {
            return 'products';
        }

        if (routeName.startsWith('directory-search-results')) {
            return routeName.includes('suppliers') ? 'suppliers' : 'products';
        }

        if (routeName === 'supplier') {
            return 'suppliers';
        }

        if (routeName === 'shone-product-details') {
            return 'products';
        }

        return 'products';
    }

    function resetTermSuggestion() {
        Object.assign(querySuggest, {
            newSearchTerm: null,
            originalSearchTerm: null
        });
    }

    function badQueryMessage() {
        return searchMeta.searchTermMessage;
    }

    function resetBadQueryMessage() {
        searchMeta.searchTermMessage = null;
    }

    watch(
        [preSearchIsReady, searchIsReady, preSearchIsLoading, searchIsLoading], 
        ([preSearchIsReadyVal, searchIsReadyVal, preSearchIsLoadingVal, searchIsLoadingVal]) => {
            searchIsReadyState[postFilters.search_type] = preSearchIsReadyVal || searchIsReadyVal;
            searchIsLoadingState[postFilters.search_type] = preSearchIsLoadingVal || searchIsLoadingVal;
        }
    );

    return {
        getFilters, 
        postFilters, 
        results, 
        resultCounts, 
        searchIsReadyState, 
        searchIsLoadingState,
        hasFilters, 
        hasResults, 
        hasProductResults, 
        hasSupplierResults,
        isDefaultSearchParams,
        adjustedSearchResultTotal,
        showEmptyResults,
        getFiltersApplied,
        requestManualSearch,
        preSearch, 
        search, 
        loadResults,
        selectQueryAndPreSearch, 
        resetFilters,
        searchView,
        searchTypeByRouteName,
        querySuggest,
        resetTermSuggestion,
        badQueryMessage,
        resetBadQueryMessage,
        searchCompleted,
        searchTerm
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useDirectorySearchStore, import.meta.hot));
}
